import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Trash2, ChevronDown, ChevronUp, CheckCircle2, Circle, MessageSquare, Send, Search, Users, Plus, X, FileText, Upload, Calendar, Lock, StickyNote } from 'lucide-react';
import { projectsAPI, messagesAPI, usersAPI, customersAPI } from '../../services/api';
import './AdminSections.css';

const STAGES = [
  'Στάδιο Παραγγελίας', 'Στάδιο Βαφείου', 'Στάδιο Παραλαβής Υλικών',
  'Στάδιο Εκφόρτωσης', 'Στάδιο Μέτρα Κοπής', 'Στάδιο Παραγωγής',
  'Στάδιο Συσκευασίας', 'Στάδιο Εγκατάστασης', 'Ολοκληρωμένο'
];

const getDeliveryColor = (deliveryDate) => {
  if (!deliveryDate) return null;
  const now = new Date();
  const delivery = new Date(deliveryDate);
  const daysLeft = Math.ceil((delivery - now) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 0) return { color: '#000', bg: '#e0e0e0', label: 'Εκπρόθεσμο' };
  if (daysLeft <= 30) return { color: '#9C27B0', bg: '#f3e5f5', label: `${daysLeft} μέρες` };
  if (daysLeft <= 60) return { color: '#f44336', bg: '#ffebee', label: `${daysLeft} μέρες` };
  if (daysLeft <= 90) return { color: '#FF9800', bg: '#fff3e0', label: `${daysLeft} μέρες` };
  return { color: '#4CAF50', bg: '#e8f5e9', label: `${daysLeft} μέρες` };
};

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectProgress, setProjectProgress] = useState({});
  const [projectMessages, setProjectMessages] = useState({});
  const [projectFiles, setProjectFiles] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('progress');
  const [searchCode, setSearchCode] = useState('');
  const [engineers, setEngineers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [createForm, setCreateForm] = useState({
    project_code: '', client_name: '', title: '', description: '',
    address: '', area: '', systems: '', color: '', customer_id: '', delivery_date: ''
  });
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadProjects();
    loadEngineers();
    loadCustomers();
  }, []);

  const loadEngineers = async () => {
    try { const res = await usersAPI.getAll(); setEngineers(res.data.filter(u => u.role === 'engineer')); } catch (e) {}
  };
  const loadCustomers = async () => {
    try { const res = await customersAPI.getAll(); setCustomers(res.data); } catch (e) {}
  };
  const loadProjects = async () => {
    try { const res = await projectsAPI.getAll(); setProjects(res.data); } catch (e) {} finally { setLoading(false); }
  };

  const toggleProject = async (project) => {
    if (expandedProject === project.id) { setExpandedProject(null); return; }
    setExpandedProject(project.id);
    setActiveSubTab('progress');
    loadProjectData(project.id);
  };

  const loadProjectData = async (projectId) => {
    try {
      const [progressRes, msgsRes, filesRes] = await Promise.all([
        projectsAPI.getProgress(projectId),
        messagesAPI.getByProject(projectId),
        projectsAPI.getFiles(projectId)
      ]);
      setProjectProgress(prev => ({ ...prev, [projectId]: progressRes.data }));
      setProjectMessages(prev => ({ ...prev, [projectId]: msgsRes.data }));
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
    } catch (e) {}
  };

  const handleToggleStage = async (projectId, stageName) => {
    try {
      await projectsAPI.updateProgress(projectId, stageName);
      const [progressRes, projectRes] = await Promise.all([projectsAPI.getProgress(projectId), projectsAPI.getAll()]);
      setProjectProgress(prev => ({ ...prev, [projectId]: progressRes.data }));
      setProjects(projectRes.data);
    } catch (e) { alert('Σφάλμα'); }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Διαγραφή έργου;')) return;
    try { await projectsAPI.delete(projectId); setExpandedProject(null); loadProjects(); } catch (e) { alert('Σφάλμα'); }
  };

  const handleSendMessage = async (projectId) => {
    if (!newMessage.trim()) return;
    try {
      await messagesAPI.send({ project_id: projectId, content: newMessage });
      setNewMessage('');
      const msgsRes = await messagesAPI.getByProject(projectId);
      setProjectMessages(prev => ({ ...prev, [projectId]: msgsRes.data }));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { alert('Σφάλμα'); }
  };

  const handleAcceptOffer = async (projectId) => {
    if (!window.confirm('Αποδοχή προσφοράς; Αυτή η ενέργεια δεν αναιρείται.')) return;
    try { await projectsAPI.acceptOffer(projectId); loadProjects(); } catch (e) { alert(e.response?.data?.detail || 'Σφάλμα'); }
  };

  const handleAddNote = async (projectId) => {
    if (!newNote.trim()) return;
    try { await projectsAPI.addNote(projectId, newNote); setNewNote(''); loadProjects(); } catch (e) { alert('Σφάλμα'); }
  };

  const handleFileUpload = async (projectId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      await projectsAPI.uploadFile(projectId, file);
      const filesRes = await projectsAPI.getFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
    } catch (e) { alert('Σφάλμα'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleDeleteFile = async (projectId, fileId) => {
    if (!window.confirm('Διαγραφή αρχείου;')) return;
    try {
      await projectsAPI.deleteFile(projectId, fileId);
      const filesRes = await projectsAPI.getFiles(projectId);
      setProjectFiles(prev => ({ ...prev, [projectId]: filesRes.data }));
    } catch (e) { alert('Σφάλμα'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const selectedCustomer = customers.find(c => c.id === createForm.customer_id);
      const data = {
        ...createForm,
        client_name: selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : createForm.client_name || 'N/A',
        title: createForm.title || createForm.project_code
      };
      await projectsAPI.create(data);
      setShowCreateModal(false);
      setCreateForm({ project_code: '', client_name: '', title: '', description: '', address: '', area: '', systems: '', color: '', customer_id: '', delivery_date: '' });
      loadProjects();
    } catch (e) { alert(e.response?.data?.detail || 'Σφάλμα'); }
    finally { setCreating(false); }
  };

  const getMaxDeliveryDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 120);
    return d.toISOString().split('T')[0];
  };

  const isStageCompleted = (projectId, stageName) => {
    const progress = projectProgress[projectId] || [];
    const entry = progress.find(p => p.status === stageName);
    return entry?.completed || false;
  };

  const getEngineerName = (engineerId) => {
    const eng = engineers.find(e => e.id === engineerId);
    return eng ? eng.full_name || eng.username : '-';
  };

  const getCustomerName = (customerId) => {
    const c = customers.find(c => c.id === customerId);
    return c ? `${c.first_name} ${c.last_name}` : '';
  };

  const getStatusColor = (s) => {
    const idx = STAGES.indexOf(s);
    if (idx === STAGES.length - 1) return '#4CAF50';
    if (idx >= 5) return '#FF9800';
    return '#2196F3';
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = !searchCode || p.project_code.toLowerCase().includes(searchCode.toLowerCase()) || p.client_name.toLowerCase().includes(searchCode.toLowerCase());
    const matchesEngineer = selectedEngineer === 'all' || p.engineer_id === selectedEngineer;
    return matchesSearch && matchesEngineer;
  });

  if (loading) return <div className="admin-section"><p style={{ textAlign: 'center', padding: '3rem' }}>Φόρτωση...</p></div>;

  return (
    <div className="admin-section" data-testid="admin-project-management">
      <div className="section-header">
        <h2><FolderOpen size={24} /> Διαχείριση Έργων</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)} data-testid="admin-create-project-btn">
          <Plus size={18} /> Νέο Έργο
        </button>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', minWidth: 280 }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input type="text" value={searchCode} onChange={(e) => setSearchCode(e.target.value)}
            placeholder="Αναζήτηση..." style={{ width: '100%', padding: '0.875rem 0.875rem 0.875rem 2.5rem', border: '1px solid #d0d0d0', fontSize: '1rem', fontFamily: 'inherit' }}
            data-testid="admin-search-project" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={18} color="#667eea" />
          <select value={selectedEngineer} onChange={(e) => setSelectedEngineer(e.target.value)}
            style={{ padding: '0.875rem 1rem', border: '1px solid #d0d0d0', fontSize: '0.95rem', fontFamily: 'inherit', minWidth: 200, background: '#fff' }}
            data-testid="admin-engineer-filter">
            <option value="all">Όλοι οι Μηχανικοί</option>
            {engineers.map(eng => <option key={eng.id} value={eng.id}>{eng.full_name || eng.username}</option>)}
          </select>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="empty-state"><FolderOpen size={48} /><p>Δεν υπάρχουν έργα</p></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredProjects.map((project) => {
            const deliveryInfo = getDeliveryColor(project.delivery_date);
            return (
              <div key={project.id} style={{ border: '1px solid #e0e0e0', background: '#fff', borderLeft: deliveryInfo ? `4px solid ${deliveryInfo.color}` : '1px solid #e0e0e0' }} data-testid={`admin-project-${project.project_code}`}>
                <div onClick={() => toggleProject(project)}
                  style={{ padding: '1rem 1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', background: expandedProject === project.id ? '#f5f5ff' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <span className="project-code">{project.project_code}</span>
                    <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{project.title}</span>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>- {project.client_name}</span>
                    {project.engineer_id && <span style={{ color: '#667eea', fontSize: '0.75rem', background: 'rgba(102,126,234,0.08)', padding: '0.15rem 0.5rem', borderRadius: 3 }}>{getEngineerName(project.engineer_id)}</span>}
                    {project.offer_accepted && <span style={{ color: '#4CAF50', fontSize: '0.75rem', background: '#e8f5e9', padding: '0.15rem 0.5rem', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}><Lock size={10} /> Αποδεκτή</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    {deliveryInfo && <span style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500, background: deliveryInfo.bg, color: deliveryInfo.color, borderRadius: 3, whiteSpace: 'nowrap' }}><Calendar size={12} style={{ verticalAlign: 'middle' }} /> {deliveryInfo.label}</span>}
                    <span style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500, background: `${getStatusColor(project.status)}15`, color: getStatusColor(project.status), borderRadius: 3, whiteSpace: 'nowrap' }}>{project.status}</span>
                    {expandedProject === project.id ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
                  </div>
                </div>

                {expandedProject === project.id && (
                  <div style={{ borderTop: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', background: '#fafafa', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {[{ id: 'progress', label: 'Στάδια' }, { id: 'details', label: 'Λεπτομέρειες' }, { id: 'files', label: 'Αρχεία' }, { id: 'notes', label: 'Σημειώσεις' }, { id: 'chat', label: 'Chat' }].map(tab => (
                          <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
                            style={{ padding: '0.75rem 1.25rem', border: 'none', borderBottom: activeSubTab === tab.id ? '2px solid #667eea' : '2px solid transparent', background: 'transparent', color: activeSubTab === tab.id ? '#667eea' : '#666', fontWeight: activeSubTab === tab.id ? 600 : 400, cursor: 'pointer', fontSize: '0.85rem' }}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => handleDelete(project.id)}
                        style={{ padding: '0.4rem 0.75rem', margin: '0.5rem', border: '1px solid rgba(244,67,54,0.3)', background: 'rgba(244,67,54,0.05)', color: '#d32f2f', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Trash2 size={14} /> Διαγραφή
                      </button>
                    </div>

                    <div style={{ padding: '1.5rem' }}>
                      {/* Progress */}
                      {activeSubTab === 'progress' && (
                        <div>
                          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>Τσεκάρετε τα στάδια:</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            {STAGES.map((stage) => {
                              const completed = isStageCompleted(project.id, stage);
                              return (
                                <div key={stage} onClick={() => handleToggleStage(project.id, stage)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: completed ? '#e8f5e9' : '#fff', border: completed ? '1px solid #a5d6a7' : '1px solid #e0e0e0', borderRadius: 4, cursor: 'pointer' }}
                                  title={completed ? 'Κλικ για ξετίκαρισμα' : 'Κλικ για ολοκλήρωση'}
                                >
                                  {completed ? <CheckCircle2 size={20} color="#4caf50" /> : <Circle size={20} color="#bbb" />}
                                  <span style={{ fontSize: '0.9rem', fontWeight: completed ? 500 : 400, color: completed ? '#2e7d32' : '#333' }}>{stage}</span>
                                  {completed && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#66bb6a' }}>OK</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Details */}
                      {activeSubTab === 'details' && (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 600 }}>
                            <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Πελάτης</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{project.client_name}</p></div>
                            <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Κωδικός</span><p style={{ margin: '0.2rem 0 0', fontWeight: 500 }}>{project.project_code}</p></div>
                            {project.address && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Διεύθυνση</span><p style={{ margin: '0.2rem 0 0' }}>{project.address}</p></div>}
                            {project.area && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Περιοχή</span><p style={{ margin: '0.2rem 0 0' }}>{project.area}</p></div>}
                            {project.systems && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Συστήματα</span><p style={{ margin: '0.2rem 0 0' }}>{project.systems}</p></div>}
                            {project.color && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Χρώμα</span><p style={{ margin: '0.2rem 0 0' }}>{project.color}</p></div>}
                            {project.delivery_date && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Ημ/νία Παράδοσης</span><p style={{ margin: '0.2rem 0 0' }}>{new Date(project.delivery_date).toLocaleDateString('el-GR')}</p></div>}
                            {project.customer_id && <div><span style={{ fontSize: '0.8rem', color: '#999' }}>Πελάτης CRM</span><p style={{ margin: '0.2rem 0 0' }}>{getCustomerName(project.customer_id)}</p></div>}
                          </div>

                          {/* Offer Acceptance */}
                          <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', background: '#fafafa' }}>
                            {project.offer_accepted ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <Lock size={20} color="#4CAF50" />
                                <div>
                                  <p style={{ margin: 0, fontWeight: 600, color: '#2e7d32' }}>Προσφορά Αποδεκτή</p>
                                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#666' }}>
                                    {project.offer_accepted_at && new Date(project.offer_accepted_at).toLocaleString('el-GR')}
                                    {project.offer_accepted_by && ` - από: ${project.offer_accepted_by}`}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => handleAcceptOffer(project.id)} className="btn-primary"
                                style={{ background: '#4CAF50', border: 'none' }} data-testid={`accept-offer-${project.project_code}`}>
                                <CheckCircle2 size={18} /> Αποδοχή Προσφοράς
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Files */}
                      {activeSubTab === 'files' && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontSize: '1rem', fontWeight: 500, margin: 0 }}>Συνημμένα Αρχεία</h3>
                            <label className="btn-primary" style={{ cursor: 'pointer', opacity: uploadingFile ? 0.5 : 1 }}>
                              <Upload size={16} /> {uploadingFile ? '...' : 'Ανέβασμα'}
                              <input type="file" onChange={(e) => handleFileUpload(project.id, e)} style={{ display: 'none' }} disabled={uploadingFile} />
                            </label>
                          </div>
                          {(projectFiles[project.id] || []).length === 0 ? (
                            <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν αρχεία</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {(projectFiles[project.id] || []).map(f => (
                                <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', border: '1px solid #e0e0e0', background: '#fafafa' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <FileText size={18} color="#667eea" />
                                    <div>
                                      <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>{f.filename}</p>
                                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#999' }}>{(f.file_size / 1024).toFixed(1)} KB - {new Date(f.uploaded_at).toLocaleDateString('el-GR')}</p>
                                    </div>
                                  </div>
                                  <button onClick={() => handleDeleteFile(project.id, f.id)} className="btn-icon danger"><Trash2 size={16} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Notes */}
                      {activeSubTab === 'notes' && (
                        <div>
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                            <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddNote(project.id)}
                              placeholder="Προσθήκη σημείωσης..."
                              style={{ flex: 1, padding: '0.75rem', border: '1px solid #d0d0d0', fontSize: '0.9rem', fontFamily: 'inherit' }}
                              data-testid={`note-input-${project.project_code}`} />
                            <button onClick={() => handleAddNote(project.id)} className="btn-primary" data-testid={`note-add-${project.project_code}`}>
                              <Plus size={16} /> Προσθήκη
                            </button>
                          </div>
                          {(project.notes_list || []).length === 0 ? (
                            <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν σημειώσεις</p>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {[...(project.notes_list || [])].reverse().map((note, idx) => (
                                <div key={note.id || idx} style={{ padding: '0.75rem 1rem', border: '1px solid #e0e0e0', background: '#fafafa', borderLeft: '3px solid #667eea' }}>
                                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#333' }}>{note.content}</p>
                                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#999' }}>
                                    {note.created_by} - {new Date(note.created_at).toLocaleString('el-GR')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Chat */}
                      {activeSubTab === 'chat' && (
                        <div>
                          <div style={{ height: 260, overflowY: 'auto', border: '1px solid #e0e0e0', padding: '1rem', marginBottom: '1rem', background: '#fafafa' }}>
                            {(projectMessages[project.id] || []).length === 0 ? (
                              <p style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Δεν υπάρχουν μηνύματα</p>
                            ) : (
                              (projectMessages[project.id] || []).map(msg => {
                                const user = JSON.parse(localStorage.getItem('user') || '{}');
                                const isOwn = msg.sender_id === user.id;
                                return (
                                  <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', marginBottom: '0.75rem' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#999', marginBottom: 2 }}>{msg.sender_name}</span>
                                    <div style={{ background: isOwn ? '#667eea' : '#fff', color: isOwn ? '#fff' : '#333', padding: '0.5rem 1rem', borderRadius: 12, maxWidth: '70%', border: isOwn ? 'none' : '1px solid #e0e0e0', fontSize: '0.9rem' }}>
                                      {msg.content}
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: '#bbb', marginTop: 2 }}>{new Date(msg.created_at).toLocaleString('el-GR')}</span>
                                  </div>
                                );
                              })
                            )}
                            <div ref={messagesEndRef} />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(project.id)}
                              placeholder="Μήνυμα..." style={{ flex: 1, padding: '0.75rem', border: '1px solid #d0d0d0', fontSize: '0.9rem', fontFamily: 'inherit' }} />
                            <button onClick={() => handleSendMessage(project.id)} className="btn-primary"><Send size={18} /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay-admin" onClick={() => setShowCreateModal(false)}>
          <div className="modal-admin" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h3>Δημιουργία Νέου Έργου</h3>
              <button onClick={() => setShowCreateModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="modal-form" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Αρ. Παραγγελίας *</label>
                  <input type="text" value={createForm.project_code} onChange={(e) => setCreateForm({ ...createForm, project_code: e.target.value })} required placeholder="π.χ. ΠΑΡ-2025-001" data-testid="admin-project-code" />
                </div>
                <div className="form-group-admin">
                  <label>Τίτλος *</label>
                  <input type="text" value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} required placeholder="Τίτλος έργου" data-testid="admin-project-title" />
                </div>
              </div>

              <div className="form-group-admin">
                <label>Πελάτης</label>
                <select value={createForm.customer_id} onChange={(e) => setCreateForm({ ...createForm, customer_id: e.target.value })}
                  style={{ padding: '0.75rem', border: '1px solid #d0d0d0', fontSize: '0.95rem', fontFamily: 'inherit', width: '100%' }}
                  data-testid="admin-project-customer">
                  <option value="">-- Χωρίς πελάτη --</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Διεύθυνση</label>
                  <input type="text" value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} placeholder="Διεύθυνση" />
                </div>
                <div className="form-group-admin">
                  <label>Περιοχή</label>
                  <input type="text" value={createForm.area} onChange={(e) => setCreateForm({ ...createForm, area: e.target.value })} placeholder="Περιοχή" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group-admin">
                  <label>Συστήματα</label>
                  <input type="text" value={createForm.systems} onChange={(e) => setCreateForm({ ...createForm, systems: e.target.value })} placeholder="Συστήματα αλουμινίου" />
                </div>
                <div className="form-group-admin">
                  <label>Χρώμα</label>
                  <input type="text" value={createForm.color} onChange={(e) => setCreateForm({ ...createForm, color: e.target.value })} placeholder="π.χ. RAL 7016" />
                </div>
              </div>

              <div className="form-group-admin">
                <label>Ημ/νία Παράδοσης (max 120 μέρες)</label>
                <input type="date" value={createForm.delivery_date}
                  onChange={(e) => setCreateForm({ ...createForm, delivery_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]} max={getMaxDeliveryDate()}
                  style={{ padding: '0.75rem', border: '1px solid #d0d0d0', fontSize: '0.95rem', fontFamily: 'inherit', width: '100%' }}
                  data-testid="admin-project-delivery" />
              </div>

              <div className="form-group-admin">
                <label>Περιγραφή</label>
                <textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} rows={2} placeholder="Περιγραφή έργου" />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Ακύρωση</button>
                <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Δημιουργία...' : 'Δημιουργία Έργου'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
